<script lang="ts" setup>
import { reactive, computed, watch, ref, onMounted, toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { AccountDetails, AccountCreateRequest, AccountUpdateRequest } from '@/types'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid, useGeo, usePostcodeState, getAllCountryCodesData } from '@/composables'
import { useDirtyFormGuard } from '@/composables/useDirtyFormGuard'
import { STATUS_CODES } from '@/constants'
import { formatPhone, parsePhone, parsePastedPhone } from '@/utils/phone'
import { type CountryData } from 'country-codes-list'
import BaseSlider from '@/components/base/BaseSlider.vue'

const { t } = useI18n()

const props = defineProps<{
  user: AccountDetails | null
  mode: 'create' | 'edit'
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const notifyStore = useNotifyStore()

// Geo data for countries and subdivisions
const {
  countries,
  subdivisions,
  loadCountries,
  loadSubdivisions,
  subdivisionsLoading
} = useGeo()

// Phone country code data
const countryCodes = ref<CountryData[]>([])
const phoneCountryData = ref<CountryData | null>(null)
const phoneLocal = ref('')

// Get list of calling codes for paste detection
const knownCallingCodes = computed(() =>
  countryCodes.value.map((c) => c.countryCallingCode)
)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formRef = ref()
const data = reactive({
  loading: false,
  form: {} as AccountDetails,
  errors: {} as Record<string, string>
})

// Track original data for dirty detection
const originalData = ref<AccountDetails | null>(null)
const isDirty = computed(() => {
  if (!originalData.value) return false
  return JSON.stringify(data.form) !== JSON.stringify(originalData.value)
})
useDirtyFormGuard('admin-user-edit', isDirty)

// State suggestion from postcode - stateRef must be writable for usePostcodeState to update it
const stateRef = computed({
  get: () => data.form.state,
  set: (val) => { data.form.state = val }
})
const countryRef = toRef(() => data.form.country_iso)
const cityRef = toRef(() => data.form.city)
const { fetchStateFromPostcode } = usePostcodeState(stateRef, {
  onlyWhenEmpty: true,
  countryRef,
  cityRef,
  useCode: true  // Use ISO 3166-2 codes (e.g., "DE-BY") instead of names
})

// Load countries on mount
onMounted(() => {
  loadCountries()
  // Load phone country codes
  countryCodes.value = getAllCountryCodesData()
  // Set default to Germany
  phoneCountryData.value = countryCodes.value.find((c) => c.countryCode === 'DE') || null
})

// Parse phone from form data (called when user changes or form resets)
const parsePhoneFromForm = () => {
  const phone = data.form.phone
  if (phone && phone.includes('.')) {
    const parsed = parsePhone(phone)
    const matchingCountry = countryCodes.value.find(
      (c) => '+' + c.countryCallingCode === parsed.countryCode
    )
    if (matchingCountry) {
      phoneCountryData.value = matchingCountry
    }
    phoneLocal.value = parsed.local
  } else if (phone) {
    // Plain phone number - use as local with default country
    phoneLocal.value = phone
  } else {
    phoneLocal.value = ''
  }
}

// Handle phone input for country code detection
const handlePhoneInput = () => {
  const input = phoneLocal.value

  // Check if input starts with + or 00 (international format)
  if (input.startsWith('+') || input.startsWith('00')) {
    const currentCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
    const { countryCode, local } = parsePastedPhone(input, currentCode, knownCallingCodes.value)

    // Find matching country data
    const matchingCountry = countryCodes.value.find(
      (c) => '+' + c.countryCallingCode === countryCode
    )

    if (matchingCountry) {
      phoneCountryData.value = matchingCountry
    }
    phoneLocal.value = local
  }
}

// Handle phone paste for country code detection
const handlePhonePaste = (event: ClipboardEvent) => {
  const pasted = event.clipboardData?.getData('text') || ''

  // Only intercept if it looks like a full phone number
  if (pasted.startsWith('+') || pasted.startsWith('00') || /^\d{10,}$/.test(pasted.replace(/\D/g, ''))) {
    event.preventDefault()

    const currentCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
    const { countryCode, local } = parsePastedPhone(pasted, currentCode, knownCallingCodes.value)

    // Find matching country data
    const matchingCountry = countryCodes.value.find(
      (c) => '+' + c.countryCallingCode === countryCode
    )

    if (matchingCountry) {
      phoneCountryData.value = matchingCountry
    }
    phoneLocal.value = local
  }
}

// Load subdivisions when country changes and sync phone CC
watch(
  () => data.form.country_iso,
  (newCountry, oldCountry) => {
    if (newCountry) {
      loadSubdivisions(newCountry)
      // Auto-sync phone country code with address country (if user changed it)
      if (oldCountry !== undefined && oldCountry !== newCountry && countryCodes.value.length > 0) {
        const matchingPhone = countryCodes.value.find(
          (c) => c.countryCode.toUpperCase() === newCountry.toUpperCase()
        )
        if (matchingPhone) {
          phoneCountryData.value = matchingPhone
        }
      }
    }
  },
  { immediate: true }
)

// Must be defined before watch that uses it
const resetForm = () => {
  data.form = {
    email: '',
    phone: '',
    account_type: 'person',
    active: true,
    email_verified: false,
    totp: false,
    lang: 'de'
  }
  // Reset phone CC to default
  phoneCountryData.value = countryCodes.value.find((c) => c.countryCode === 'DE') || null
  phoneLocal.value = ''
}

// Initialize form when user changes
watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      data.form = { ...newUser }
      originalData.value = { ...newUser }
      // Parse phone number into country code and local part
      parsePhoneFromForm()
    } else {
      resetForm()
      originalData.value = { ...data.form }
    }
    data.errors = {}
  },
  { immediate: true }
)

// Panel is visible when user prop is truthy (object or empty object for create)
const isVisible = computed(() => props.user !== null)

const pageTitle = computed(() => {
  if (props.mode === 'create') return t('dashboard.admin.users.edit.newUser')
  return data.form.account_type === 'organization'
    ? data.form.org || t('dashboard.admin.users.edit.editUser')
    : `${data.form.name || ''} ${data.form.surname || ''}`.trim() || t('dashboard.admin.users.edit.editUser')
})

// Validation
const validateField = (field: string, value: unknown): string | null => {
  switch (field) {
    case 'email':
      if (!value || String(value).length === 0) return t('dashboard.admin.users.edit.validation.emailRequired')
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value))) return t('dashboard.admin.users.edit.validation.emailInvalid')
      break
    case 'phone':
      if (!value || String(value).length === 0) return t('dashboard.admin.users.edit.validation.phoneRequired')
      break
    case 'name':
      if (data.form.account_type === 'person' && (!value || String(value).length === 0))
        return t('dashboard.admin.users.edit.validation.nameRequired')
      break
    case 'surname':
      if (data.form.account_type === 'person' && (!value || String(value).length === 0))
        return t('dashboard.admin.users.edit.validation.surnameRequired')
      break
    case 'org':
      if (data.form.account_type === 'organization' && (!value || String(value).length === 0))
        return t('dashboard.admin.users.edit.validation.companyRequired')
      break
    case 'line1':
      if (!value || String(value).length === 0) return t('dashboard.admin.users.edit.validation.streetRequired')
      break
    case 'city':
      if (!value || String(value).length === 0) return t('dashboard.admin.users.edit.validation.cityRequired')
      break
    case 'postcode':
      if (!value || String(value).length === 0) return t('dashboard.admin.users.edit.validation.postcodeRequired')
      break
    case 'state':
      if (!value) return t('dashboard.admin.users.edit.validation.stateRequired')
      break
    case 'country_iso':
      if (!value || String(value).length === 0) return t('dashboard.admin.users.edit.validation.countryRequired')
      if (String(value).length > 2) return t('dashboard.admin.users.edit.validation.countryMaxLength')
      break
  }
  return null
}

const validate = (): boolean => {
  data.errors = {}
  const fields = ['email', 'phone', 'name', 'surname', 'org', 'line1', 'city', 'postcode', 'state', 'country_iso']
  let isValid = true

  for (const field of fields) {
    const error = validateField(field, data.form[field as keyof AccountDetails])
    if (error) {
      data.errors[field] = error
      isValid = false
    }
  }

  return isValid
}

const handleSave = async (createAnother = false) => {
  if (!validate()) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('dashboard.admin.users.edit.validation.validationError'),
      message: t('dashboard.admin.users.edit.validation.checkFields'),
      alertType: 'error'
    })
    return
  }

  data.loading = true

  // Format phone number from country code selector and local number
  const phoneCode = '+' + (phoneCountryData.value?.countryCallingCode || '49')
  const phone = formatPhone(phoneCode, phoneLocal.value)

  if (props.mode === 'create') {
    // Build create request with only the fields the API expects
    const createData: AccountCreateRequest = {
      email: data.form.email,
      phone,
      name: data.form.name || undefined,
      surname: data.form.surname || undefined,
      org: data.form.org || undefined,
      line1: data.form.line1 || '',
      line2: data.form.line2 || undefined,
      city: data.form.city || '',
      postcode: data.form.postcode || '',
      country_iso: (data.form.country_iso || 'DE').toUpperCase(),
      state: data.form.state || '',
      ust_id: data.form.ust_id || undefined,
      account_type: data.form.account_type || 'person',
      active: data.form.active ?? true,
      email_verified: data.form.email_verified ?? false,
      lang: data.form.lang || 'de'
    }

    // Pre-flight Zod validation
    const validation = AccountCreateRequest.safeParse(createData)
    if (!validation.success) {
      console.error('UserEdit:Create validation failed:', validation.error.issues)
      data.loading = false
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'error',
        title: t('dashboard.admin.users.edit.validation.validationError'),
        message: `Validierungsfehler: ${validation.error.issues.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`
      })
      return
    }

    useAxios<AccountCreateRequest, { id: number; temp_password: string }>('POST', '/account/create', true, createData)
      .then((res) => {
        if (!res) return

        if (res.status === STATUS_CODES.CREATED) {
          notifyStore.createNotify({
            id: uuid(),
            title: t('common.success'),
            message: t('dashboard.admin.users.edit.notifications.created'),
            alertType: 'success'
          })
          emits('saved')

          if (createAnother) {
            resetForm()
            data.errors = {}
          } else {
            emits('close')
          }
        }
      })
      .catch((error) => {
        handleAxiosError(error, { logPrefix: 'UserEdit:Create' })
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.error'),
          message: getErrorMessage(error),
          alertType: 'error'
        })
      })
      .finally(() => {
        data.loading = false
      })
  } else {
    // Build update request
    const updateData: AccountUpdateRequest = {
      email: data.form.email,
      phone,
      name: data.form.name || undefined,
      surname: data.form.surname || undefined,
      org: data.form.org || undefined,
      line1: data.form.line1 || undefined,
      line2: data.form.line2 || undefined,
      city: data.form.city || undefined,
      postcode: data.form.postcode || undefined,
      country_iso: data.form.country_iso?.toUpperCase(),
      state: data.form.state || undefined,
      ust_id: data.form.ust_id || undefined,
      account_type: data.form.account_type,
      active: data.form.active,
      email_verified: data.form.email_verified,
      lang: data.form.lang,
      max_team_members: data.form.max_team_members,
      max_sub_customers: data.form.max_sub_customers
    }

    // Pre-flight Zod validation
    const validation = AccountUpdateRequest.safeParse(updateData)
    if (!validation.success) {
      console.error('UserEdit:Update validation failed:', validation.error.issues)
      data.loading = false
      notifyStore.createNotify({
        id: uuid(),
        alertType: 'error',
        title: t('dashboard.admin.users.edit.validation.validationError'),
        message: `Validierungsfehler: ${validation.error.issues.map(e => `${e.path.join('.')}: ${e.message}`).join(', ')}`
      })
      return
    }

    useAxios<AccountUpdateRequest, null>('POST', `/account/${data.form.id}`, true, updateData)
      .then((res) => {
        if (!res) return

        if (res.status === STATUS_CODES.OK) {
          notifyStore.createNotify({
            id: uuid(),
            title: t('common.success'),
            message: t('dashboard.admin.users.edit.notifications.updated'),
            alertType: 'success'
          })
          emits('saved')
          emits('close')
        }
      })
      .catch((error) => {
        handleAxiosError(error, { logPrefix: 'UserEdit:Update' })
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.error'),
          message: getErrorMessage(error),
          alertType: 'error'
        })
      })
      .finally(() => {
        data.loading = false
      })
  }
}

const handleClose = () => {
  emits('close')
}
</script>

<template>
  <BaseSlider
    :model-value="isVisible"
    width="600"
    :loading="data.loading"
    @update:model-value="handleClose"
  >
    <template #header>
      <div class="header-content">
        <div class="header-icon">
          <v-icon :icon="mode === 'create' ? 'mdi-account-plus' : 'mdi-account-edit'" size="24" />
        </div>
        <div class="header-text">
          <h2 class="header-title">{{ pageTitle }}</h2>
          <p class="header-subtitle">{{ mode === 'create' ? $t('dashboard.admin.users.edit.createUser') : $t('dashboard.admin.users.edit.editUserData') }}</p>
        </div>
      </div>
    </template>

    <!-- Content -->
            <!-- Status Section -->
            <div class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.users.edit.status') }}</h3>
              <div class="status-toggles">
                <label class="toggle-item">
                  <input type="checkbox" v-model="data.form.active" class="toggle-input" />
                  <span class="toggle-switch"></span>
                  <span class="toggle-label">{{ $t('dashboard.admin.users.edit.active') }}</span>
                </label>

                <label class="toggle-item">
                  <input type="checkbox" v-model="data.form.email_verified" class="toggle-input" />
                  <span class="toggle-switch"></span>
                  <span class="toggle-label">{{ $t('dashboard.admin.users.edit.emailVerified') }}</span>
                </label>

                <div v-if="data.form.totp" class="totp-badge">
                  <v-icon icon="mdi-shield-check" size="16" />
                  <span>{{ $t('dashboard.admin.users.edit.twoFactorActive') }}</span>
                </div>
              </div>
            </div>

            <!-- Account Type & Role -->
            <div class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.users.edit.accountType') }}</h3>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.accountTypeLabel') }}</label>
                  <div class="type-selector">
                    <button
                      class="type-btn"
                      :class="{ active: data.form.account_type === 'person' }"
                      @click="data.form.account_type = 'person'"
                    >
                      <v-icon icon="mdi-account" size="20" />
                      <span>{{ $t('dashboard.admin.users.edit.person') }}</span>
                    </button>
                    <button
                      class="type-btn"
                      :class="{ active: data.form.account_type === 'organization' }"
                      @click="data.form.account_type = 'organization'"
                    >
                      <v-icon icon="mdi-domain" size="20" />
                      <span>{{ $t('dashboard.admin.users.edit.organization') }}</span>
                    </button>
                  </div>
                </div>

              </div>
            </div>

            <!-- Limits (edit mode only) -->
            <div v-if="mode === 'edit'" class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.users.edit.limits') }}</h3>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.maxTeamMembers') }}</label>
                  <input
                    v-model.number="data.form.max_team_members"
                    type="number"
                    min="0"
                    class="form-input"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.maxSubCustomers') }}</label>
                  <input
                    v-model.number="data.form.max_sub_customers"
                    type="number"
                    min="0"
                    class="form-input"
                  />
                </div>
              </div>
            </div>

            <!-- Contact Information -->
            <div class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.users.edit.contactData') }}</h3>
              <div class="form-row">
                <div class="form-group" :class="{ error: data.errors.email }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.email') }}</label>
                  <input
                    v-model="data.form.email"
                    type="email"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.emailPlaceholder')"
                  />
                  <span v-if="data.errors.email" class="error-text">{{ data.errors.email }}</span>
                </div>

                <div class="form-group" :class="{ error: data.errors.phone }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.phone') }}</label>
                  <div class="phone-input-wrapper">
                    <v-autocomplete
                      v-model="phoneCountryData"
                      :items="countryCodes"
                      item-title="countryNameEn"
                      item-value="countryCallingCode"
                      return-object
                      density="compact"
                      variant="outlined"
                      hide-details
                      class="phone-code-select"
                      :menu-props="{ maxHeight: 300, width: 'auto', minWidth: '280px' }"
                    >
                      <template #selection="{ item }">
                        <span class="phone-code-selection">
                          <span class="flag">{{ item.flag }}</span>
                          <span>+{{ item.countryCallingCode }}</span>
                        </span>
                      </template>
                      <template #item="{ props: itemProps, item }">
                        <v-list-item v-bind="itemProps">
                          <template #prepend>
                            <span class="flag-large">{{ item.flag }}</span>
                          </template>
                          <template #title>{{ item.countryNameEn }}</template>
                          <template #subtitle>+{{ item.countryCallingCode }}</template>
                        </v-list-item>
                      </template>
                    </v-autocomplete>
                    <input
                      v-model="phoneLocal"
                      type="tel"
                      class="form-input phone-local"
                      :placeholder="$t('dashboard.admin.users.edit.phonePlaceholder')"
                      @input="handlePhoneInput"
                      @paste="handlePhonePaste"
                    />
                  </div>
                  <span v-if="data.errors.phone" class="error-text">{{ data.errors.phone }}</span>
                </div>
              </div>
            </div>

            <!-- Personal/Organization Info -->
            <div class="form-section">
              <h3 class="section-title">
                {{ data.form.account_type === 'organization' ? $t('dashboard.admin.users.edit.companyData') : $t('dashboard.admin.users.edit.personalData') }}
              </h3>

              <div v-if="data.form.account_type === 'organization'" class="form-row">
                <div class="form-group full-width" :class="{ error: data.errors.org }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.companyName') }}</label>
                  <input
                    v-model="data.form.org"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.companyPlaceholder')"
                  />
                  <span v-if="data.errors.org" class="error-text">{{ data.errors.org }}</span>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group" :class="{ error: data.errors.name }">
                  <label class="form-label">{{ data.form.account_type === 'person' ? $t('dashboard.admin.users.edit.firstName') : $t('dashboard.admin.users.edit.firstNameContact') }}</label>
                  <input
                    v-model="data.form.name"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.firstNamePlaceholder')"
                  />
                  <span v-if="data.errors.name" class="error-text">{{ data.errors.name }}</span>
                </div>

                <div class="form-group" :class="{ error: data.errors.surname }">
                  <label class="form-label">{{ data.form.account_type === 'person' ? $t('dashboard.admin.users.edit.lastName') : $t('dashboard.admin.users.edit.lastNameContact') }}</label>
                  <input
                    v-model="data.form.surname"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.lastNamePlaceholder')"
                  />
                  <span v-if="data.errors.surname" class="error-text">{{ data.errors.surname }}</span>
                </div>
              </div>

              <div v-if="data.form.account_type === 'organization'" class="form-row">
                <div class="form-group">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.vatId') }}</label>
                  <input
                    v-model="data.form.ust_id"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.vatIdPlaceholder')"
                  />
                </div>
              </div>
            </div>

            <!-- Address -->
            <div class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.users.edit.address') }}</h3>

              <div class="form-row">
                <div class="form-group full-width" :class="{ error: data.errors.line1 }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.street') }}</label>
                  <input
                    v-model="data.form.line1"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.streetPlaceholder')"
                  />
                  <span v-if="data.errors.line1" class="error-text">{{ data.errors.line1 }}</span>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group full-width">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.addressSupplement') }}</label>
                  <input
                    v-model="data.form.line2"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.addressSupplementPlaceholder')"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group" :class="{ error: data.errors.country_iso }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.country') }}</label>
                  <v-autocomplete
                    v-model="data.form.country_iso"
                    :items="countries"
                    item-title="name"
                    item-value="code"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :menu-props="{ maxHeight: 300 }"
                    :placeholder="$t('dashboard.admin.users.edit.countrySelect')"
                  >
                    <template #selection="{ item }">
                      <span class="country-selection">
                        <span class="flag">{{ item.flag }}</span>
                        <span>{{ item.name }}</span>
                      </span>
                    </template>
                    <template #item="{ props: itemProps, item }">
                      <v-list-item v-bind="itemProps">
                        <template #prepend>
                          <span class="flag-large">{{ item.flag }}</span>
                        </template>
                        <template #title>{{ item.name }}</template>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  <span v-if="data.errors.country_iso" class="error-text">{{ data.errors.country_iso }}</span>
                </div>

                <div class="form-group" :class="{ error: data.errors.postcode }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.postcode') }}</label>
                  <input
                    v-model="data.form.postcode"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.postcodePlaceholder')"
                    @blur="fetchStateFromPostcode(data.form.postcode || '')"
                  />
                  <span v-if="data.errors.postcode" class="error-text">{{ data.errors.postcode }}</span>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group" :class="{ error: data.errors.city }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.city') }}</label>
                  <input
                    v-model="data.form.city"
                    type="text"
                    class="form-input"
                    :placeholder="$t('dashboard.admin.users.edit.cityPlaceholder')"
                  />
                  <span v-if="data.errors.city" class="error-text">{{ data.errors.city }}</span>
                </div>

                <div class="form-group" :class="{ error: data.errors.state }">
                  <label class="form-label">{{ $t('dashboard.admin.users.edit.state') }}</label>
                  <v-autocomplete
                    v-model="data.form.state"
                    :items="subdivisions"
                    :disabled="subdivisionsLoading || subdivisions.length === 0"
                    :loading="subdivisionsLoading"
                    item-title="name"
                    item-value="code"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                    :menu-props="{ maxHeight: 300 }"
                    :placeholder="subdivisionsLoading ? 'Laden...' : $t('dashboard.admin.users.edit.stateSelect')"
                  />
                  <span v-if="data.errors.state" class="error-text">{{ data.errors.state }}</span>
                </div>
              </div>
            </div>

    <template #footer>
      <button
        v-if="mode === 'create'"
        type="button"
        class="save-another-btn"
        :disabled="data.loading"
        @click="handleSave(true)"
      >
        <v-icon icon="mdi-plus" size="18" />
        <span>{{ $t('dashboard.admin.users.edit.saveAndNew') }}</span>
      </button>
      <button
        type="button"
        class="save-btn"
        :disabled="data.loading"
        @click="handleSave(false)"
      >
        <v-progress-circular v-if="data.loading" indeterminate size="18" width="2" />
        <v-icon v-else icon="mdi-content-save" size="18" />
        <span>{{ $t('dashboard.admin.users.edit.save') }}</span>
      </button>
    </template>
  </BaseSlider>
</template>

<style lang="scss" scoped>
/* Header content (used in #header slot) */
.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(0, 176, 255, 0.15), rgba(31, 226, 212, 0.1));
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: 12px;
  color: #00b0ff;
}

.header-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 4px;
}

.header-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

/* Footer buttons */
.save-btn,
.save-another-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.save-btn {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.2), rgba(102, 187, 106, 0.15));
  border: 1px solid rgba(76, 175, 80, 0.35);
  color: #4caf50;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(76, 175, 80, 0.3), rgba(102, 187, 106, 0.25));
    border-color: rgba(76, 175, 80, 0.5);
  }
}

.save-another-btn {
  background: linear-gradient(135deg, rgba(0, 176, 255, 0.2), rgba(31, 226, 212, 0.15));
  border: 1px solid rgba(0, 176, 255, 0.35);
  color: #00b0ff;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(0, 176, 255, 0.3), rgba(31, 226, 212, 0.25));
    border-color: rgba(0, 176, 255, 0.5);
  }
}

.form-section {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);
}

/* Status Toggles */
.status-toggles {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 12px;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: rgba(200, 218, 255, 0.5);
    border-radius: 50%;
    transition: all 0.2s ease;
  }
}

.toggle-input:checked + .toggle-switch {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);

  &::after {
    left: 22px;
    background: #4CAF50;
  }
}

.toggle-label {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.totp-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 8px;
  font-size: 0.8rem;
  color: #4CAF50;
}

/* Type & Role Selectors */
.type-selector,
.type-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &.active {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.3);
    color: #00B0FF;
  }
}

/* Form */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  & + .form-row {
    margin-top: 16px;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &.full-width {
    grid-column: 1 / -1;
  }

  &.error {
    .form-input,
    .form-select {
      border-color: rgba(244, 67, 54, 0.5);
    }
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.form-input,
.form-select {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 176, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.form-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23c8daff' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;

  option {
    background: #1a2744;
    color: #fff;
  }
}

.error-text {
  font-size: 0.75rem;
  color: #F44336;
}

/* Country Selection */
.country-selection {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;

  span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.flag {
  font-size: 1.1rem;
}

.flag-large {
  font-size: 1.4rem;
  margin-right: 8px;
}

/* Phone Input */
.phone-input-wrapper {
  display: flex;
  gap: 10px;
}

.phone-code-select {
  width: 120px;
  min-width: 120px;
  flex-shrink: 0;
}

.phone-local {
  flex: 1;
  min-width: 0;
}

.phone-code-selection {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  min-width: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .save-btn,
  .save-another-btn,
  .toggle-switch,
  .toggle-switch::after,
  .type-btn,
  .form-input,
  .form-select {
    transition: none;
  }
}
</style>

<!-- Unscoped styles for Vuetify components - SSR compatibility -->
<style lang="scss">
/* Prevent v-autocomplete from becoming multi-line when focused */
.form-section .v-field__input {
  flex-wrap: nowrap !important;
  overflow: hidden;
}

.form-section .v-autocomplete__selection {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
</style>
